import React, { useMemo, useEffect, useRef } from 'react'

import { SimpleGrid, Text, Tooltip } from '@chakra-ui/react'
import {
  Flex,
  Stack,
  Form,
  Button,
  Switch,
  Select,
  Typography,
  Icon,
} from '@pnld/components-web'

import Map from '@/components/Map'
import {
  regionOptions,
  dependencyOptions,
  locationOptions,
  educationStageOptions,
  sphereOptions,
  stateOptions,
  stateToRegionMap,
  regionEnumToStringMap,
  stateEnumToSiglaMap,
} from '@/data/dashboard'

import { useGeoMapFilterController } from './controller'
import { GeoMapFilterProps, GeoMapFilterValues } from './types'

export * from './types'

const FormValuesSync: React.FC<{
  formikProps: { values: GeoMapFilterValues }
  onSync: (values: GeoMapFilterValues) => void
}> = ({ formikProps, onSync }) => {
  const prevRef = useRef(formikProps.values)

  useEffect(() => {
    const prev = prevRef.current
    const next = formikProps.values

    const changed =
      prev.region !== next.region ||
      prev.states.length !== next.states.length ||
      prev.states.some((v, i) => v !== next.states[i]) ||
      prev.cities.length !== next.cities.length ||
      prev.cities.some((v, i) => v !== next.cities[i])

    if (changed) {
      prevRef.current = next
      onSync(next)
    }
  }, [formikProps.values, onSync])

  return null
}

export const GeoMapFilter: React.FC<GeoMapFilterProps> = ({
  predictionYears,
  censusYears,
  onChangeFilter,
}) => {
  const {
    cities,
    schools,
    yearGrades,
    formRef,
    formValues,
    initialValues,
    selectRefs,
    handleOnSubmit,
    handleOnClear,
    handleOnFilter,
    syncFormValues,
    isCitiesDisabled,
    isSchoolsDisabled,
    isEducationStagesDisabled,
    isYearGradesDisabled,
    isHasPnldStagesDisabled,
  } = useGeoMapFilterController({
    predictionYears,
    censusYears,
    onChangeFilter,
  })

  const currentValues = formRef.current?.values || formValues

  const mapProps = useMemo(() => {
    const region = currentValues.region
      ? regionEnumToStringMap[currentValues.region]
      : undefined

    const selectedStates = (currentValues.states || [])
      .map(stateValue => stateEnumToSiglaMap[stateValue])
      .filter(Boolean)

    const selectedRegions =
      selectedStates.length > 0 ? [] : region ? [region] : []

    return {
      region,
      selectedStates,
      selectedRegions,
    }
  }, [currentValues.region, currentValues.states])

  const filteredStateOptions = useMemo(() => {
    if (!currentValues.region) {
      return stateOptions
    }

    return stateOptions.filter(
      state => stateToRegionMap[state.value] === currentValues.region
    )
  }, [currentValues.region])

  return (
    <Flex bg="brand.primary.light_2" borderRadius="md" p={4} gap={6}>
      <Flex
        flex={2}
        align="flex-start"
        justify="center"
        data-testid="map-container"
      >
        <Map
          region={mapProps.region}
          selectedStates={mapProps.selectedStates}
          selectedRegions={mapProps.selectedRegions}
        />
      </Flex>
      <Flex
        flex={3}
        sx={{
          form: {
            width: '100%',
          },
        }}
      >
        <Form
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          enableReinitialize={true}
        >
          {(formikProps: { values: GeoMapFilterValues }) => (
            <>
              <FormValuesSync
                formikProps={formikProps}
                onSync={syncFormValues}
              />
              <Stack flex={2} gap={4}>
                {predictionYears && (
                  <Stack>
                    <div data-testid="prediction-year-select">
                      <Select
                        selectRef={ref =>
                          (selectRefs.current.predictionYear = ref)
                        }
                        name="predictionYears"
                        label="Ano de Predição"
                        options={predictionYears.value || []}
                        placeholder={
                          predictionYears.isMulti
                            ? 'Selecione os anos de predição'
                            : 'Selecione o ano de predição'
                        }
                        isMulti={predictionYears.isMulti}
                        multiValueLimitDisplayed={3}
                        disabled={predictionYears.isLoading}
                        {...({
                          isLoading: predictionYears.isLoading,
                        } as { isLoading: boolean })}
                      />
                    </div>
                    {predictionYears.isMulti && (
                      <Typography
                        data-testid="prediction-year-select-max-alert"
                        variant="b-sm"
                        color="brand.neutral.dark_1"
                        lineHeight={1}
                      >
                        Você pode selecionar até 5 anos para realizar
                        comparações entre períodos.
                      </Typography>
                    )}
                    <Typography
                      data-testid="prediction-year-select-base-alert"
                      variant="b-sm"
                      color="brand.neutral.dark_1"
                      lineHeight={1}
                    >
                      *<b>Ano base</b>: Ano do censo utilizado para geração da
                      predição.
                    </Typography>
                  </Stack>
                )}
                {censusYears && (
                  <Stack>
                    <div data-testid="census-year-select">
                      <Select
                        selectRef={ref => (selectRefs.current.censusYear = ref)}
                        name="censusYears"
                        label="Ano do Censo"
                        options={censusYears.value || []}
                        placeholder={
                          censusYears.isMulti
                            ? 'Selecione os anos do censo'
                            : 'Selecione o ano do censo'
                        }
                        isMulti={censusYears.isMulti}
                        disabled={censusYears.isLoading}
                        {...({
                          isLoading: censusYears.isLoading,
                        } as { isLoading: boolean })}
                      />
                    </div>
                    {censusYears.isMulti && (
                      <Typography
                        data-testid="census-year-select-max-alert"
                        variant="b-sm"
                        color="brand.neutral.dark_1"
                        lineHeight={1}
                      >
                        Você pode selecionar até 5 anos para realizar
                        comparações entre períodos.
                      </Typography>
                    )}
                  </Stack>
                )}
                <SimpleGrid columns={2} spacing={4}>
                  <div data-testid="region-select">
                    <Select
                      selectRef={ref => (selectRefs.current.region = ref)}
                      label="Região"
                      name="region"
                      options={regionOptions}
                      placeholder="Todos"
                      disabled={formikProps.values.states?.length > 0}
                    />
                  </div>
                  <div data-testid="dependency-select">
                    <Select
                      selectRef={ref => (selectRefs.current.dependency = ref)}
                      label="Dependência"
                      name="dependencies"
                      options={dependencyOptions}
                      placeholder="Todos"
                      isMulti
                    />
                  </div>
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={4}>
                  <div data-testid="states-select">
                    <Select
                      selectRef={ref => (selectRefs.current.states = ref)}
                      label="Estado"
                      name="states"
                      options={filteredStateOptions}
                      placeholder="Todos"
                      isMulti
                    />
                  </div>
                  <div data-testid="locations-select">
                    <Select
                      selectRef={ref => (selectRefs.current.location = ref)}
                      label="Localização"
                      name="locations"
                      options={locationOptions}
                      placeholder="Todos"
                      isMulti
                    />
                  </div>
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={4}>
                  <div data-testid="cities-select">
                    <Select
                      selectRef={ref => (selectRefs.current.cities = ref)}
                      label="Município"
                      name="cities"
                      options={cities.value || []}
                      placeholder="Todos"
                      disabled={isCitiesDisabled || cities.isLoading}
                      isMulti
                      {...({ isLoading: cities.isLoading } as {
                        isLoading: boolean
                      })}
                    />
                  </div>
                  <div data-testid="spheres-select">
                    <Select
                      selectRef={ref => (selectRefs.current.spheres = ref)}
                      label="Esferas"
                      name="spheres"
                      options={sphereOptions}
                      placeholder="Todos"
                      isMulti
                    />
                  </div>
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={4}>
                  <div data-testid="schools-select">
                    <Select
                      selectRef={ref => (selectRefs.current.schools = ref)}
                      label="Escolas"
                      name="schools"
                      options={schools.value || []}
                      placeholder="Todos"
                      disabled={isSchoolsDisabled || schools.isLoading}
                      isMulti
                      {...({
                        isLoading: schools.isLoading,
                      } as { isLoading: boolean })}
                    />
                  </div>
                  <div data-testid="education-stages-select">
                    <Select
                      selectRef={ref =>
                        (selectRefs.current.education_group = ref)
                      }
                      label="Etapa de ensino"
                      name="educationStages"
                      options={educationStageOptions}
                      placeholder="Todos"
                      disabled={isEducationStagesDisabled}
                      isMulti
                    />
                  </div>
                </SimpleGrid>
                <SimpleGrid columns={2} spacing={4}>
                  <Flex align="center" gap={2}>
                    <Text
                      fontWeight="semibold"
                      fontSize="2xs"
                      data-testid="pnld-stages-select-label"
                    >
                      Etapas atendidas pelo PNLD
                    </Text>
                    <Tooltip
                      label="Limite sua busca para as etapas de ensino atendidas pelo PNLD."
                      hasArrow
                      placement="top"
                    >
                      <span>
                        <Icon name="circle-info" size="2xs" />
                      </span>
                    </Tooltip>
                    <Switch
                      name="hasPndlService"
                      size="lg"
                      checked={false}
                      disabled={isHasPnldStagesDisabled}
                    />
                    <Typography variant="b-sm" color="brand.neutral.dark_2">
                      {currentValues.hasPndlService ? 'Sim' : 'Não'}
                    </Typography>
                  </Flex>
                  <div data-testid="year-grades-select">
                    <Select
                      selectRef={ref => (selectRefs.current.year_series = ref)}
                      label="Ano / Série"
                      name="yearGrades"
                      options={yearGrades.value || []}
                      placeholder="Todos"
                      disabled={isYearGradesDisabled || yearGrades.isLoading}
                      isMulti
                      {...({
                        isLoading: yearGrades.isLoading,
                      } as { isLoading: boolean })}
                    />
                  </div>
                </SimpleGrid>
                <Flex justify="flex-end">
                  <Flex justify="space-between" gap={2}>
                    <Button
                      iconName="filter-circle-xmark"
                      variant="highlight"
                      label="Limpar filtro"
                      border="square"
                      onClick={handleOnClear}
                      data-testid="clean-filters-button"
                    />
                    <Button
                      iconName="filter"
                      label="Filtrar"
                      border="square"
                      onClick={handleOnFilter}
                      data-testid="apply-filters-button"
                    />
                  </Flex>
                </Flex>
              </Stack>
            </>
          )}
        </Form>
      </Flex>
    </Flex>
  )
}
